import { useEffect, useRef } from 'react';

interface MixedAudioPlayerProps {
  src: string;
}

export function MixedAudioPlayer({ src }: MixedAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    if (isInitializedRef.current) return;
    if (typeof window === 'undefined') return;
    const AudioCtx =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;

    try {
      const audioContext = new AudioCtx();
      const source = audioContext.createMediaElementSource(audioEl);
      const splitter = audioContext.createChannelSplitter(2);
      const leftGain = audioContext.createGain();
      const rightGain = audioContext.createGain();
      const monoGain = audioContext.createGain();
      const merger = audioContext.createChannelMerger(2);

      // Blend L+R into mono, then output the same signal to both ears.
      leftGain.gain.value = 0.5;
      rightGain.gain.value = 0.5;
      source.connect(splitter);
      splitter.connect(leftGain, 0);
      splitter.connect(rightGain, 1);
      leftGain.connect(monoGain);
      rightGain.connect(monoGain);
      monoGain.connect(merger, 0, 0);
      monoGain.connect(merger, 0, 1);
      merger.connect(audioContext.destination);

      const resumeContext = () => {
        if (audioContext.state === 'suspended') {
          void audioContext.resume();
        }
      };
      audioEl.addEventListener('play', resumeContext);
      isInitializedRef.current = true;
    } catch (error) {
      console.error('MixedAudioPlayer: failed to initialize downmix', {
        src,
        error,
      });
    }
  }, [src]);

  return (
    <audio
      ref={audioRef}
      controls
      crossOrigin="anonymous"
      style={{ width: '100%' }}
      src={src}
    />
  );
}
